/**
 * Bild-Platzhalter der Signatur für die ANZEIGE auflösen.
 *
 * Gemeldet am 25.08.2026 von Paul: „Warum sind hier die Logos nicht drin,
 * sondern nur platzhalter?" — im Portal stand wörtlich `{{BILD:logo}}` statt
 * des Bildes.
 *
 * 🔴 Die Auflösung gab es schon — aber nur an EINER Stelle, in der Vorschau
 * des Mandanten-Formulars. Antwort- und Weiterleiten-Fenster hängten
 * `signature_html` roh ins DOM, und dort stand der Platzhalter dann als Text.
 * Vierte Ausprägung desselben Musters an einem Tag (drei Buchungsdialoge,
 * zwei Frontend-Listen, Reiter lesen/schreiben) — deshalb hier einmal, für
 * alle.
 *
 * 🔑 Das ist reine Darstellung. Beim Versand ersetzt `signatur_bilder`
 * (services/imap/_send.py) dieselben Platzhalter durch CID-Anhänge — nur so
 * zeigen Outlook und Gmail die Bilder. Die Höhe hier muss zu `BILD_HOEHE`
 * dort passen, sonst zeigt die Vorschau andere Größenverhältnisse als die
 * Mail beim Empfänger.
 */

export type SignaturBild = { name: string; hoehe?: number | null }

/** Wie `BILD_HOEHE` in services/imap/_send.py. */
export const BILD_HOEHE = 45

const PLATZHALTER = /\{\{BILD:([^}]{1,40})\}\}/g

export function platzhalterAufloesen(
  html: string,
  opts: { slug: string; logoSet?: boolean; bilder?: SignaturBild[]; fehlendZeigen?: boolean },
): string {
  const { slug, logoSet, bilder, fehlendZeigen = false } = opts
  return (html || "").replace(PLATZHALTER, (_m, roh: string) => {
    const name = roh.trim()
    const bild = (q: string, h: number) =>
      `<img src="${q}" alt="${name}" style="height:${h}px;width:auto;vertical-align:middle">`
    const treffer = (bilder || []).find((b) => b.name === name)
    if (treffer) {
      return bild(
        `/api/v1/tenants/${encodeURIComponent(slug)}/signature-images/${encodeURIComponent(name)}`,
        treffer.hoehe || BILD_HOEHE,
      )
    }
    // `logo` ist reserviert: greift auf das alte Einzel-Logo-Feld zurück.
    if (name === "logo" && logoSet) {
      return bild(`/api/v1/tenants/${encodeURIComponent(slug)}/signature-logo`, BILD_HOEHE)
    }
    // Im Mandanten-Formular ist der Hinweis nützlich (Tippfehler im Namen).
    // Überall sonst wäre er Lärm — dort verschwindet der Platzhalter still,
    // genau wie beim Versand.
    return fehlendZeigen
      ? `<span style="color:#b91c1c">[kein Bild „${name}“ hinterlegt]</span>`
      : ""
  })
}

/**
 * Platzhalter aus einem beliebigen Mailtext entfernen.
 *
 * Für gespeicherte Mails, in deren Text der Platzhalter gelandet ist — auf
 * Prod betrifft das drei Stück. Sie sind nicht mehr zu reparieren (die Mail
 * ist raus), aber niemand muss sie so lesen.
 */
export function platzhalterEntfernen(text: string): string {
  return (text || "").replace(PLATZHALTER, "")
}
